/**
 * O que fazer com um 401 (F-S5, T-57).
 *
 * O ramo de 401 do `apiFetch` chamava `clearSessionTokens()` e disparava
 * `linkflow:session-expired` **mesmo quando não havia token nenhum**: o
 * visitante anônimo de `/v/{slug}` "perdia" uma sessão que nunca teve, e — a
 * metade de segurança — um usuário logado em outra aba era arrastado junto, já
 * que os tokens moram em `localStorage` e um clique numa ferramenta os apagava.
 *
 * A regra é uma só: **só limpe quando havia token para limpar.** Sem access e
 * sem refresh não há sessão a renovar nem a encerrar; o 401 é só a resposta
 * honesta de uma rota que pede login.
 *
 * Fora do `api.ts` porque ele não carrega em `node --test` (`import.meta.env`)
 * e esta decisão é o que precisa de teste.
 */

export type Recovery401 =
  | { kind: "retry"; accessToken: string }
  | { kind: "anonymous" }
  | { kind: "expired" };

export interface Recovery401Deps {
  /** O access token que foi na requisição — `null` quando não havia. */
  accessToken: string | null;
  /** O refresh guardado — `null` quando não há sessão para renovar. */
  refreshToken: string | null;
  /** Renova a sessão e devolve o access novo; rejeita quando não dá. */
  refresh: () => Promise<string>;
  clearTokens: () => void;
  /** Avisa a tela (`linkflow:session-expired`) — só quando havia sessão. */
  announceExpired: () => void;
}

export const SEM_SESSAO_MENSAGEM = "Entre na sua conta para usar esta ferramenta.";
export const SESSAO_EXPIRADA_MENSAGEM = "Session expired";

/** Havia uma sessão nesta aba — access em mãos ou refresh guardado. */
export function haviaSessao(accessToken: string | null, refreshToken: string | null): boolean {
  return Boolean(accessToken) || Boolean(refreshToken);
}

export async function recoverFrom401(deps: Recovery401Deps): Promise<Recovery401> {
  if (!haviaSessao(deps.accessToken, deps.refreshToken)) {
    // Anônimo: nada a renovar, nada a apagar, ninguém a avisar.
    return { kind: "anonymous" };
  }
  try {
    return { kind: "retry", accessToken: await deps.refresh() };
  } catch {
    deps.clearTokens();
    deps.announceExpired();
    return { kind: "expired" };
  }
}

/** A mensagem que o chamador lança quando o 401 não tem retry. */
export function mensagemDo401(kind: "anonymous" | "expired"): string {
  return kind === "anonymous" ? SEM_SESSAO_MENSAGEM : SESSAO_EXPIRADA_MENSAGEM;
}
